import { Group, Stack, Text } from "@mantine/core"
import { IconAlertCircle } from "@tabler/icons-react"
import { InformationRequestStatus } from "@tfg/db/browser"
import { LabelWithTooltip } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ContactSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactSelect.tsx"
import { DebouncedTextInput } from "~/pages/SupplierPortal/TraceabilityRequests/components/DebouncedTextInput"
import { ProductionSiteSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteSelect.tsx"
import { useTraceabilityOperations } from "~/pages/SupplierPortal/TraceabilityRequests/context/useTraceabilityOperations"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import { isOperationOwner } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"

import { BatchPopoverOperationItemType } from "../components/BatchPopoverOperationItem"

export const useBatchPopoverTarget = <T,>(
  type: BatchPopoverOperationItemType,
  value: T,
  onChange: (value: T) => void,
  open: () => void
) => {
  const { t } = useTranslation("traceability")
  const { status, user, isAdmin, request } = useInformationRequestContext()
  const {
    operationItem,
    isTransferred,
    mandatoryReference,
    isReinforcedTraceability,
  } = useTraceabilityOperations()
  const isOwner =
    user && operationItem.operation
      ? isOperationOwner(operationItem.operation, user)
      : false

  const itemType = useGetItemTypeLabel(operationItem.item.type)

  const targetComponents: Record<
    BatchPopoverOperationItemType,
    JSX.Element | null
  > = useMemo(() => {
    const genericProps = {
      disabled: status !== InformationRequestStatus.OPENED,
      w: "100%",
      error: !value,
    }

    const showReferenceAndPO = mandatoryReference || !isTransferred

    const isReferenceError =
      isTransferred &&
      mandatoryReference &&
      (!value || (typeof value === "string" && value.length === 0))

    const isRequiredReference = isTransferred && mandatoryReference
    const isOwnerOnlyFieldDisabled = genericProps.disabled || !isOwner
    return {
      contact: (
        <ContactSelect
          {...genericProps}
          disabled={isOwnerOnlyFieldDisabled}
          contactId={value as string}
          onContactSelected={(contactId) => {
            onChange(contactId as T)
            open()
          }}
          userId={user?.id}
          isAdmin={isAdmin}
          informationRequestId={request.original.id}
          tooltip={t("tooltips.contact")}
          isReinforcedTraceability={isReinforcedTraceability}
        />
      ),
      productionSite: (
        <Stack gap="xs">
          <ProductionSiteSelect
            {...genericProps}
            disabled={isOwnerOnlyFieldDisabled}
            productionSiteId={typeof value === "string" ? value : null}
            isRequired={!isTransferred}
            onProductionSiteSelected={(productionSiteId) => {
              onChange(productionSiteId as T)
              open()
            }}
            tooltip={t("tooltips.productionSite")}
          />
          {operationItem.isDuplicateProductionSite && (
            <Group wrap="nowrap" align="start" gap={4}>
              <Text fz="xs" c="var(--mantine-color-orange-8)">
                <span style={{ marginRight: 4, verticalAlign: "middle" }}>
                  <IconAlertCircle
                    size={12}
                    color="var(--mantine-color-orange-8)"
                  />
                </span>
                {t("operation.duplicateProductionSite")}
              </Text>
            </Group>
          )}
        </Stack>
      ),
      reference: showReferenceAndPO ? (
        <DebouncedTextInput
          {...genericProps}
          inputValue={typeof value === "string" ? value : ""}
          label={
            <LabelWithTooltip
              justify="space-between"
              label={t("operation.reference", { type: itemType })}
              hoverHelperText={t("tooltips.reference", { type: itemType })}
              withAsterisk={isRequiredReference}
            />
          }
          labelProps={{ w: "100%" }}
          onChange={(value) => onChange(value as T)}
          onOpen={open}
          error={isReferenceError}
        />
      ) : null,
      purchaseOrderRef: showReferenceAndPO ? (
        <DebouncedTextInput
          {...genericProps}
          inputValue={typeof value === "string" ? value : ""}
          label={
            <LabelWithTooltip
              justify="space-between"
              label={t("operation.purchaseOrderRef", { type: itemType })}
              hoverHelperText={t("tooltips.purchaseOrderRef", {
                type: itemType,
              })}
              withAsterisk={isRequiredReference}
            />
          }
          labelProps={{ w: "100%" }}
          error={isReferenceError}
          onChange={(value) => onChange(value as T)}
          onOpen={open}
        />
      ) : null,
    }
  }, [
    isAdmin,
    itemType,
    isTransferred,
    mandatoryReference,
    onChange,
    open,
    status,
    t,
    user?.id,
    value,
    request.original.id,
    isReinforcedTraceability,
    operationItem.isDuplicateProductionSite,
    isOwner,
  ])

  return targetComponents[type] || null
}

const useGetItemTypeLabel = (type: string): string => {
  const { t } = useTranslation("traceability")
  return type === "product"
    ? t("operation.product")
    : type === "component"
    ? t("operation.component")
    : t("operation.material")
}
